import { isObject, isFunction, getBooleanFromValue } from '@/global/utils/utils'
import { dateService, FORMATS } from '@/global/services/date'
import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions.js'

const { buildConfig } = useTableService()

const UI_VIEW = 'temporary_regulations_requests'
const TAB_KEY = 'requests'
const API_CONFIG = {
  get: {
    module: 'road-maintenance',
    route: 'incoming-requests/traffic-regulation'
  }
}
const DEFAULT_CELL_EMPTY_VALUE = null

const STATUSES_ROUTE = 'traffic-regulation/statuses'

// Status options for the `status` / `offer_status` filters are fetched once
// upfront and injected directly into the filter config, so the autocomplete
// fields no longer trigger their own API requests every time they are opened.
const statusItems = ref([])
const offerStatusItems = ref([])
let statusItemsRequested = false

function fetchStatusItems () {
  if (statusItemsRequested) {
    return
  }
  statusItemsRequested = true

  Promise.all([
    api('road-maintenance').get(STATUSES_ROUTE, { types: ['INCOMING_REQUEST'] }),
    api('road-maintenance').get(STATUSES_ROUTE, { types: ['INCOMING_REQUEST_OFFER'] })
  ])
    .then(([statusResponse, offerStatusResponse]) => {
      statusItems.value = statusResponse?.data || []
      offerStatusItems.value = offerStatusResponse?.data || []
    })
    .catch((exception) => {
      console.error(exception)
      statusItemsRequested = false
    })
}

export function useTable (options) {
  const {
    trans,
    router,
    openMoveToWorkOrdersDialog,
    closeMoveToWorkOrdersDialog,
    displayDatetimeFormat,
    sqlDatetimeFormat,
    attachmentsCellIconComponent,
    openFilesPreviewDialogFn,
    closeInfoDialogFn,
    openInfoDialogFn,
    setInfoDialogLoaderFn,
    setMoveToWorkOrdersDialogLoaderFn
  } = options

  fetchStatusItems()

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.temporary_regulation')
      },
      {
        text: trans('road-maintenance.requests'),
        bolded: true
      }
    ]
  }))

  const getPredictedPeriod = (data) => {
    // The API returns `planned_start`/`planned_end` (see IncomingRequestDTO::toArray()),
    // not `date_start`/`date_end`.
    const { planned_start: plannedStart, planned_end: plannedEnd } = data || {}
    if (!plannedStart || !plannedEnd) {
      return DEFAULT_CELL_EMPTY_VALUE
    }

    return `${dateService.formatCustom(plannedStart, sqlDatetimeFormat, displayDatetimeFormat)} - ${dateService.formatCustom(plannedEnd, sqlDatetimeFormat, displayDatetimeFormat)}`
  }

  const onPreviewAttachment = (file) => {
    const { url } = file || {}
    if (!url) {
      return
    }

    window.open(url, '_blank')
  }

  const onAttachmentsPreview = (event) => {
    const { name } = event?.rowData || {}
    openFilesPreviewDialogFn({
      title: `${name || trans('road-maintenance/temporary_regulation/requests.e_regulation')} - ${trans('road-maintenance/temporary_regulation.attachments')}`,
      files: event?.cellData || [],
      onDownload: onPreviewAttachment,
      noFilesText: trans('base.no_data')
    })
  }

  const cellsConfig = computed(() => ({
    created_at: {
      custom_display_value: (data) => {
        return data?.created_at ? dateService.formatCustom(data?.created_at, sqlDatetimeFormat, displayDatetimeFormat) : DEFAULT_CELL_EMPTY_VALUE
      }
    },
    processed_at: {
      custom_display_value: (data) => {
        return data?.processed_at ? dateService.formatCustom(data?.processed_at, sqlDatetimeFormat, displayDatetimeFormat) : DEFAULT_CELL_EMPTY_VALUE
      }
    },
    actual_end: {
      custom_display_value: (data) => {
        return data?.actual_end ? dateService.formatCustom(data?.actual_end, sqlDatetimeFormat, displayDatetimeFormat) : DEFAULT_CELL_EMPTY_VALUE
      }
    },
    source: {
      custom_display_value: (data) => {
        return trans(data?.source?.description || DEFAULT_CELL_EMPTY_VALUE)
      }
    },
    predicted_period: {
      custom_display_value: (data) => getPredictedPeriod(data)
    },
    status: {
      type: 'status',
      custom_display_value: (data) => {
        if (!data?.status || !isObject(data?.status)) {
          return DEFAULT_CELL_EMPTY_VALUE
        }
        return {
          ...data.status,
          ...{
            description: data.status.description ? trans(data.status.description) : '-'
          }
        }
      },
      displayValue: 'description',
      colorObjectKey: 'color',
      textColorKey: 'text',
      backgroundColorKey: 'background'
    },
    offer_status: {
      type: 'status',
      custom_display_value: (data) => {
        if (!data?.offer_status || !isObject(data?.offer_status)) {
          return DEFAULT_CELL_EMPTY_VALUE
        }
        return {
          ...data.offer_status,
          ...{
            description: data.offer_status.description ? trans(data.offer_status.description) : '-'
          }
        }
      },
      displayValue: 'description',
      colorObjectKey: 'color',
      textColorKey: 'text',
      backgroundColorKey: 'background'
    },
    attachments: {
      wrapperStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start'
      }
    }
  }))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/temporary_regulation/requests.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.eregulation_number'),
      key: 'traffic_regulation_code',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.name'),
      key: 'name',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.address'),
      key: 'address',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.status'),
      key: 'status',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.offer_status'),
      key: 'offer_status',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.city_district'),
      key: 'city_district',
      initial_value: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.predicted_period'),
      key: 'predicted_period',
      initial_value: true
    },
    {
      label: trans('road-maintenance/temporary_regulation.attachments'),
      disableDefaultClick: true,
      key: 'attachments',
      component: attachmentsCellIconComponent,
      onCellClick: onAttachmentsPreview,
      initial_value: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.source'),
      key: 'source',
      initial_value: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.created_at'),
      key: 'created_at',
      initial_value: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.processed_at'),
      key: 'processed_at',
      initial_value: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.completed_at'),
      key: 'actual_end',
      initial_value: true
    }
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'id',
        label: trans('road-maintenance/temporary_regulation/requests.id')
      },
      {
        key: 'traffic_regulation_code',
        label: trans('road-maintenance/temporary_regulation/requests.eregulation_number')
      },
      {
        key: 'name',
        label: trans('road-maintenance/temporary_regulation/requests.name')
      },
      {
        key: 'address',
        label: trans('road-maintenance/temporary_regulation/requests.address')
      },
      {
        key: 'status',
        label: trans('road-maintenance/temporary_regulation/requests.status'),
        type: 'autocomplete',
        items: statusItems.value,
        autocomplete_options: {
          custom_display_value: (item) => trans(item?.description || ''),
          allowSearch: false
        }
      },
      {
        key: 'offer_status',
        label: trans('road-maintenance/temporary_regulation/requests.offer_status'),
        type: 'autocomplete',
        items: offerStatusItems.value,
        autocomplete_options: {
          custom_display_value: (item) => trans(item?.description || ''),
          allowSearch: false
        }
      },
      {
        key: 'city_district',
        label: trans('road-maintenance/temporary_regulation/requests.city_district'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'traffic-regulation/city-districts'
        }
      },
      {
        key: 'planned_start',
        label: trans('road-maintenance/temporary_regulation/requests.planned_start'),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month',
        format: FORMATS.datetimeShort
      },
      {
        key: 'planned_end',
        label: trans('road-maintenance/temporary_regulation/requests.planned_end'),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month'
      },
      {
        key: 'source',
        label: trans('road-maintenance/temporary_regulation/requests.source')
      },
      {
        key: 'created_at',
        label: trans('road-maintenance/temporary_regulation/requests.created_at'),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month'
      },
      {
        key: 'processed_at',
        label: trans('road-maintenance/temporary_regulation/requests.processed_at'),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month'
      },
      {
        key: 'actual_end',
        label: trans('road-maintenance/temporary_regulation/requests.completed_at'),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month'
      }
    ]
  }))

  const sendToWorkOrdersHandler = async (event) => {
    try {
      const { id: requestId } = event?.data || {}
      setMoveToWorkOrdersDialogLoaderFn(true)
      if (!requestId) {
        return
      }

      await api('road-maintenance').post(`incoming-requests/traffic-regulation/${requestId}/process`)
      closeMoveToWorkOrdersDialog()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setMoveToWorkOrdersDialogLoaderFn(false)
    }
  }

  const onSendToWorkOrders = (event) => {
    if (!isFunction(openMoveToWorkOrdersDialog)) {
      return
    }

    openMoveToWorkOrdersDialog({
      data: event?.data,
      onConfirm: () => sendToWorkOrdersHandler(event)
    })
  }

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TAB_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const deleteRequestHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      setInfoDialogLoaderFn(true)
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`incoming-requests/traffic-regulation/${id}`)
      fetchTableData()
      closeInfoDialogFn()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onRequestDelete = (event) => {
    const { name } = event?.data || {}
    const nameTitle = name?.length > 20
      ? name.substring(0, 20) + '...'
      : name || ''

    openInfoDialogFn({
      title: trans('road-maintenance/temporary_regulation.request_delete') + (nameTitle ? ' - ' + nameTitle : ''),
      message: trans('road-maintenance/temporary_regulation.request_delete_confirmation'),
      onConfirm: () => deleteRequestHandler(event)
    })
  }

  const onShowDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: 'TemporaryRegulationsRequestDetails',
      params: { id: String(id) }
    })
  }

  const isDeleteActionVisible = (event) => {
    return getBooleanFromValue(event?.data?.is_deletable)
  }

  const isSendToWorkOrdersActionVisible = (event) => {
    return getBooleanFromValue(event?.data?.can_send_to_work_orders)
  }

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowDetails),
    {
      label: trans('road-maintenance/temporary_regulation/requests.send_to_work_orders'),
      icon: 'mdi-send-variant',
      click: onSendToWorkOrders,
      visible: isSendToWorkOrdersActionVisible
    },
    { ...deleteAction(trans, onRequestDelete), visible: isDeleteActionVisible }
  ]))

  return computed(() => {
    return {
      tableConfig: buildConfig({
        uiView: UI_VIEW,
        key: TAB_KEY,
        apiConfig: API_CONFIG,
        rowDblClick: onShowDetails,
        tableConfig: {
          cellsConfig: cellsConfig.value,
          headers: headers.value,
          actionsConfig: actionsConfig.value,
          filtersConfig: filtersConfig.value
        }
      }),
      breadcrumbs: breadcrumbs.value
    }
  })
}
